import { Link } from '@tanstack/react-router'
import type { CSSProperties, ReactNode } from 'react'
import { Button } from '@/components/atoms'
import { referFriend, rentersMoveCouple } from '@/shared/assets'

export type PromoCardVariant = 'coral' | 'indigo'

export type PromoCardLayout = 'stacked' | 'compact'

export type PromoCardAction = { label: string } & (
  | { to: string; onClick?: never }
  | { onClick: () => void; to?: never }
)

export interface PromoCardProps {
  variant: PromoCardVariant
  layout?: PromoCardLayout
  title: string
  titleHighlight: string
  description: ReactNode
  action: PromoCardAction
  className?: string
  testId?: string
}

const STROKED_CLASS = 'relative z-1'

const titleStroke = (color: string): CSSProperties => ({
  WebkitTextStroke: `2px var(${color})`,
  paintOrder: 'stroke fill',
})

interface LayoutConfig {
  card: string
  group: string
  title: string
  textBlock: string
  description: string
}

const LAYOUTS: Record<PromoCardLayout, LayoutConfig> = {
  stacked: {
    card: 'md:mt-11 md:justify-end md:pt-0 md:pb-6',
    group: 'md:gap-0.5',
    title: 'md:text-[2rem]',
    textBlock: 'md:gap-2',
    description: 'md:tracking-[0.01rem]',
  },
  compact: {
    card: 'md:flex-row md:gap-6 md:px-6 md:py-4.5',
    group: 'md:gap-1',
    title: 'md:text-2xl',
    textBlock: 'md:shrink-0 md:gap-2',
    description: 'md:text-sm md:leading-4.5',
  },
}

interface VariantLayoutConfig {
  group: string
  illustration: string
  titleBox: string
  title: string
  highlightGap: string
  textBlock: string
  description: string
}

const VARIANTS: Record<
  PromoCardVariant,
  {
    card: string
    illustration: (className: string) => ReactNode
    title: { className: string; style?: CSSProperties }
    highlight: { className: string; style?: CSSProperties }
    layouts: Record<PromoCardLayout, VariantLayoutConfig>
  }
> = {
  coral: {
    card: 'border-coral-100 bg-coral-50',
    illustration: (className) => (
      <img
        src={referFriend}
        alt=""
        aria-hidden
        className={`h-44.5 w-69.75 ${className}`}
      />
    ),
    title: { className: '' },
    highlight: {
      className: STROKED_CLASS,
      style: titleStroke('--color-coral-50'),
    },
    layouts: {
      stacked: {
        group: 'md:-mt-10.5',
        illustration: 'md:h-25.75 md:w-[10.09rem]',
        titleBox: '',
        title: '',
        highlightGap: 'md:mt-[0.19rem]',
        textBlock: '',
        description: 'md:max-w-[17.5625rem]',
      },
      compact: {
        group: '',
        illustration: 'md:h-[3.64rem] md:w-[5.706rem]',
        titleBox: 'md:h-10.25! md:pt-[0.14rem]',
        title: '',
        highlightGap: 'md:mt-[0.09rem]',
        textBlock: 'md:w-57.5',
        description: '',
      },
    },
  },
  indigo: {
    card: 'border-indigo-100 bg-indigo-50',
    illustration: (className) => (
      <div
        aria-hidden
        className={`h-40.25 w-74.75 overflow-hidden ${className}`}
      >
        <img src={rentersMoveCouple} alt="" className="w-full max-w-none" />
      </div>
    ),
    title: {
      className: `-translate-x-[0.5rem] ${STROKED_CLASS}`,
      style: titleStroke('--color-indigo-50'),
    },
    highlight: {
      className: '',
    },
    layouts: {
      stacked: {
        group: 'md:-mt-11.25',
        illustration: 'md:h-26.25 md:w-[12.175rem]',
        titleBox: '',
        title: '',
        highlightGap: 'md:mt-[0.225rem]',
        textBlock: '',
        description: 'md:max-w-[19.375rem]',
      },
      compact: {
        group: '',
        illustration: 'md:h-15.5 md:w-[7.19rem]',
        titleBox: 'md:h-10.5! md:pt-px',
        title: 'md:-translate-x-[0.34rem]',
        highlightGap: 'md:mt-[0.28rem]',
        textBlock: 'md:w-[11.4375rem]',
        description: '',
      },
    },
  },
}

const TITLE_CLASS =
  'font-display flex h-[5.375rem] flex-col items-center pt-2.25 text-center text-[3.125rem] leading-[0.731] font-bold whitespace-nowrap text-neutral-900 md:h-16'

const HIGHLIGHT_GAP_CLASS = 'mt-[0.090625rem]'

const CTA_CLASS =
  'tracking-base h-12 w-full md:h-8 md:w-auto md:px-4 md:text-xs md:leading-4'

export function PromoCard({
  variant,
  layout = 'stacked',
  title,
  titleHighlight,
  description,
  action,
  className = '',
  testId,
}: PromoCardProps) {
  const config = VARIANTS[variant]
  const layoutConfig = LAYOUTS[layout]
  const variantLayout = config.layouts[layout]
  const ctaProps = action.to
    ? { as: Link, to: action.to }
    : { onClick: action.onClick }

  return (
    <div
      className={`mt-0 flex flex-col items-center justify-center gap-4 rounded-2xl border px-6 py-10 ${layoutConfig.card} ${config.card} ${className}`}
      data-testid={testId}
    >
      <div
        className={`flex shrink-0 flex-col items-center gap-4 ${layoutConfig.group} ${variantLayout.group}`}
      >
        {config.illustration(variantLayout.illustration)}
        <p
          className={`${TITLE_CLASS} ${layoutConfig.title} ${variantLayout.titleBox}`}
        >
          <span
            className={`block ${config.title.className} ${variantLayout.title}`}
            style={config.title.style}
          >
            {title}
          </span>
          <span
            className={`text-indigo-base block ${HIGHLIGHT_GAP_CLASS} ${variantLayout.highlightGap} ${config.highlight.className}`}
            style={config.highlight.style}
          >
            {titleHighlight}
          </span>
        </p>
      </div>

      <div
        className={`flex w-full flex-col items-center gap-6 ${layoutConfig.textBlock} ${variantLayout.textBlock}`}
      >
        <p
          className={`[&_strong]:text-coral-base text-center text-base leading-5 text-neutral-900 [&_strong]:font-bold ${layoutConfig.description} ${variantLayout.description}`}
        >
          {description}
        </p>
        <Button
          size="md"
          className={CTA_CLASS}
          data-testid={testId && `${testId}-cta`}
          {...(ctaProps as object)}
        >
          {action.label}
        </Button>
      </div>
    </div>
  )
}
